import React from 'react';
import { Row, Col, Progress, Typography, Tooltip, Space, Skeleton } from 'antd';
import { DollarOutlined, HomeOutlined, UserOutlined, ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { PerformanceSummary } from '../../../types/dashboard';
import { formatRupiah, formatNumber, formatPercent, getProgressColor, glassCardStyle } from '../utils';

const { Title, Text } = Typography;

interface SummaryCardsProps {
  summary?: PerformanceSummary | null;
  loading: boolean;
}

export const SummaryCards: React.FC<SummaryCardsProps> = ({ summary, loading }) => {
  const isCardLoading = loading || !summary;

  return (
    <Row gutter={[20, 20]} style={{ marginBottom: '24px' }}>
      {/* KPI 1: Omzet */}
      <Col xs={24} sm={12} lg={6}>
        <div style={glassCardStyle} className="glass-card-interactive">
          {isCardLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} title={false} style={{ margin: '8px 0' }} />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <Space size={6}>
                    <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Realisasi Omzet
                    </Text>
                    <Tooltip title="Nilai realisasi dari transaksi pemesanan unit (closing) yang sudah disetujui secara resmi oleh manajemen Kantor Pusat pada periode yang difilter. Transaksi pending atau rejected tidak dihitung.">
                      <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
                    </Tooltip>
                  </Space>
                  <Title level={3} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                    {formatRupiah(summary.omzet.realization)}
                  </Title>
                </div>
                <div style={{ padding: '10px', background: 'rgba(123, 170, 24, 0.15)', borderRadius: '10px' }}>
                  <DollarOutlined style={{ fontSize: '22px', color: '#7BAA18' }} />
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Target: {formatRupiah(summary.omzet.target)}</Text>
                  <Text strong style={{ color: getProgressColor(summary.omzet.percentage) }}>
                    {formatPercent(summary.omzet.percentage)}
                  </Text>
                </div>
                <Progress
                  percent={summary.omzet.percentage}
                  showInfo={false}
                  strokeColor={getProgressColor(summary.omzet.percentage)}
                  status={summary.omzet.percentage >= 100 ? "success" : "normal"}
                  strokeWidth={6}
                />
              </div>
            </>
          )}
        </div>
      </Col>

      {/* KPI 2: Unit Terjual */}
      <Col xs={24} sm={12} lg={6}>
        <div style={glassCardStyle} className="glass-card-interactive">
          {isCardLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} title={false} style={{ margin: '8px 0' }} />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <Space size={6}>
                    <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Unit Terjual
                    </Text>
                    <Tooltip title="Jumlah unit properti yang telah sukses terjual (berstatus approved/disetujui oleh manajemen Pusat) pada periode yang difilter.">
                      <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
                    </Tooltip>
                  </Space>
                  <Title level={3} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                    {formatNumber(summary.unit.realization)} <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#64748b' }}>Unit</span>
                  </Title>
                </div>
                <div style={{ padding: '10px', background: 'rgba(24, 144, 255, 0.15)', borderRadius: '10px' }}>
                  <HomeOutlined style={{ fontSize: '22px', color: '#1890ff' }} />
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Text type="secondary">Target: {formatNumber(summary.unit.target)} Unit</Text>
                  <Text strong style={{ color: getProgressColor(summary.unit.percentage) }}>
                    {formatPercent(summary.unit.percentage)}
                  </Text>
                </div>
                <Progress
                  percent={summary.unit.percentage}
                  showInfo={false}
                  strokeColor={getProgressColor(summary.unit.percentage)}
                  strokeWidth={6}
                />
              </div>
            </>
          )}
        </div>
      </Col>

      {/* KPI 3: Perolehan Leads */}
      <Col xs={24} sm={12} lg={6}>
        <div style={glassCardStyle} className="glass-card-interactive">
          {isCardLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} title={false} style={{ margin: '8px 0' }} />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <Space size={6}>
                    <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Pencapaian Leads
                    </Text>
                    <Tooltip title="Jumlah perolehan calon pembeli (leads) aktif yang terekam masuk ke dalam sistem pada periode yang difilter.">
                      <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
                    </Tooltip>
                  </Space>
                  <Title level={3} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                    {formatNumber(summary.leads.realization)} <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#64748b' }}>Leads</span>
                  </Title>
                </div>
                <div style={{ padding: '10px', background: 'rgba(250, 173, 20, 0.15)', borderRadius: '10px' }}>
                  <UserOutlined style={{ fontSize: '22px', color: '#faad14' }} />
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Text type="secondary">Target: {formatNumber(summary.leads.target)} Leads</Text>
                  <Text strong style={{ color: getProgressColor(summary.leads.percentage) }}>
                    {formatPercent(summary.leads.percentage)}
                  </Text>
                </div>
                <Progress
                  percent={summary.leads.percentage}
                  showInfo={false}
                  strokeColor={getProgressColor(summary.leads.percentage)}
                  strokeWidth={6}
                />
              </div>
            </>
          )}
        </div>
      </Col>

      {/* KPI 4: SLA Compliance Rate */}
      <Col xs={24} sm={12} lg={6}>
        <div style={glassCardStyle} className="glass-card-interactive">
          {isCardLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} title={false} style={{ margin: '8px 0' }} />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <Space size={6}>
                    <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      On-Time SLA
                    </Text>
                    <Tooltip title="Tingkat kepatuhan tim sales dalam merespons leads tepat waktu. Rumus: Jumlah Respon Tepat Waktu / Total Leads Selesai Dievaluasi * 100%. Hanya menilai leads yang sudah selesai diproses (achieved/expired), leads aktif atau dibatalkan diabaikan agar adil.">
                      <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
                    </Tooltip>
                  </Space>
                  <Title level={3} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                    {formatPercent(summary.sla_compliance_rate)}
                  </Title>
                </div>
                <div style={{ padding: '10px', background: 'rgba(255, 77, 79, 0.15)', borderRadius: '10px' }}>
                  <ClockCircleOutlined style={{ fontSize: '22px', color: '#ff4d4f' }} />
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <Text type="secondary">Standar Tim: 85%</Text>
                  <Text strong style={{ color: getProgressColor(summary.sla_compliance_rate) }}>
                    {summary.sla_compliance_rate >= 85 ? 'Sangat Bagus' : 'Butuh Evaluasi'}
                  </Text>
                </div>
                <Progress
                  percent={summary.sla_compliance_rate}
                  showInfo={false}
                  strokeColor={getProgressColor(summary.sla_compliance_rate)}
                  strokeWidth={6}
                />
              </div>
            </>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default SummaryCards;
